/**
 * versona_voice.ts — which KittenTTS voice a versona speaks with (pure,
 * 2026-09-10). "Make sure the voice matches the versona."
 *
 * KittenTTS ships eight expressive voices, four women and four men:
 *   Bella 2-f · Luna 3-f · Rosie 4-f · Kiki 5-f · Jasper 2-m · Bruno 3-m ·
 *   Hugo 4-m · Leo 5-m.
 * The voice follows the BODY the crowd draws (EnvironmentModule.avatarFor —
 * the archetype's pool keyed by the versona id, or the civ's citizen body),
 * read by eye off the generated roster: the Aurelian citizen is a silver-
 * haired man, the Borean citizen a white-haired woman, colonist A a bearded
 * man, colonist B a woman, the child a child, the merchant a heavy man, the
 * official a woman, the suited guard a man, envoy A a woman, courier B a
 * man. Each body has two candidate voices of its gender; the versona id
 * picks one and nudges the speed ±4 %, so two neighbours in the same coat
 * still sound like two people. Children get the lightest voice a shade
 * quicker; the aliens speak a shade slower. Custom (editor-authored)
 * versonas without a known body hash over all eight.
 */

export type KittenVoice =
  | 'expr-voice-2-f' | 'expr-voice-3-f' | 'expr-voice-4-f' | 'expr-voice-5-f'
  | 'expr-voice-2-m' | 'expr-voice-3-m' | 'expr-voice-4-m' | 'expr-voice-5-m';

export const KITTEN_VOICES: readonly KittenVoice[] = [
  'expr-voice-2-f', 'expr-voice-3-f', 'expr-voice-4-f', 'expr-voice-5-f',
  'expr-voice-2-m', 'expr-voice-3-m', 'expr-voice-4-m', 'expr-voice-5-m',
];

/** The friendly names KittenTTS gives them (config.json voice_aliases). */
export const VOICE_NAMES: Record<KittenVoice, string> = {
  'expr-voice-2-f': 'Bella', 'expr-voice-3-f': 'Luna', 'expr-voice-4-f': 'Rosie', 'expr-voice-5-f': 'Kiki',
  'expr-voice-2-m': 'Jasper', 'expr-voice-3-m': 'Bruno', 'expr-voice-4-m': 'Hugo', 'expr-voice-5-m': 'Leo',
};

/** The reference's per-voice speed priors (config.json). */
export const SPEED_PRIORS: Record<KittenVoice, number> = {
  'expr-voice-2-f': 0.8, 'expr-voice-2-m': 0.8, 'expr-voice-3-m': 0.8, 'expr-voice-3-f': 0.8,
  'expr-voice-4-m': 0.9, 'expr-voice-4-f': 0.8, 'expr-voice-5-m': 0.8, 'expr-voice-5-f': 0.8,
};

export type BodyGender = 'f' | 'm' | 'child';

interface BodyVoice { gender: BodyGender; voices: [KittenVoice, KittenVoice]; speed: number }

/** Body key (with or without the `avatar:` prefix) → gender, candidate voices, base speed. */
export const BODY_VOICES: Record<string, BodyVoice> = {
  civ_aurelian_citizen: { gender: 'm', voices: ['expr-voice-3-m', 'expr-voice-4-m'], speed: 0.96 },
  civ_borean_citizen:   { gender: 'f', voices: ['expr-voice-3-f', 'expr-voice-2-f'], speed: 0.96 },
  colonist_jumpsuit_a:  { gender: 'm', voices: ['expr-voice-2-m', 'expr-voice-5-m'], speed: 1.0 },
  colonist_jumpsuit_b:  { gender: 'f', voices: ['expr-voice-4-f', 'expr-voice-5-f'], speed: 1.0 },
  child_colonist:       { gender: 'child', voices: ['expr-voice-5-f', 'expr-voice-4-f'], speed: 1.12 },
  merchant_robed:       { gender: 'm', voices: ['expr-voice-3-m', 'expr-voice-2-m'], speed: 0.92 },
  official_uniform:     { gender: 'f', voices: ['expr-voice-3-f', 'expr-voice-4-f'], speed: 0.95 },
  env_suit_worker:      { gender: 'm', voices: ['expr-voice-4-m', 'expr-voice-5-m'], speed: 1.0 },
  alien_humanoid_a:     { gender: 'f', voices: ['expr-voice-2-f', 'expr-voice-5-f'], speed: 0.92 },
  alien_humanoid_b:     { gender: 'm', voices: ['expr-voice-2-m', 'expr-voice-4-m'], speed: 0.92 },
  crew_captain:         { gender: 'm', voices: ['expr-voice-3-m', 'expr-voice-3-m'], speed: 0.94 },
  crew_officer:         { gender: 'f', voices: ['expr-voice-3-f', 'expr-voice-3-f'], speed: 0.97 },
  crew_engineer:        { gender: 'm', voices: ['expr-voice-4-m', 'expr-voice-4-m'], speed: 1.0 },
  crew_medic:           { gender: 'f', voices: ['expr-voice-4-f', 'expr-voice-4-f'], speed: 0.97 },
  crew_deckhand:        { gender: 'm', voices: ['expr-voice-5-m', 'expr-voice-5-m'], speed: 1.02 },
  crew_cook:            { gender: 'm', voices: ['expr-voice-2-m', 'expr-voice-2-m'], speed: 0.95 },
  crew_quartermaster:   { gender: 'f', voices: ['expr-voice-2-f', 'expr-voice-2-f'], speed: 0.95 },
  crew_steward:         { gender: 'f', voices: ['expr-voice-5-f', 'expr-voice-5-f'], speed: 1.0 },
  // ── versona wave 4 (2026-10-08): gender read off each concept sheet; elders speak a little slower ──
  civ_aurelian_weaver_f:    { gender: 'f', voices: ['expr-voice-2-f', 'expr-voice-4-f'], speed: 0.97 },
  civ_aurelian_elder_m:     { gender: 'm', voices: ['expr-voice-3-m', 'expr-voice-2-m'], speed: 0.9 },
  civ_aurelian_elder_f:     { gender: 'f', voices: ['expr-voice-3-f', 'expr-voice-2-f'], speed: 0.9 },
  civ_aurelian_fieldhand_m: { gender: 'm', voices: ['expr-voice-4-m', 'expr-voice-5-m'], speed: 1.0 },
  civ_aurelian_artisan_f:   { gender: 'f', voices: ['expr-voice-4-f', 'expr-voice-5-f'], speed: 1.0 },
  civ_aurelian_youth_m:     { gender: 'm', voices: ['expr-voice-5-m', 'expr-voice-2-m'], speed: 1.04 },
  civ_aurelian_scholar_f:   { gender: 'f', voices: ['expr-voice-3-f', 'expr-voice-4-f'], speed: 0.95 },
  civ_borean_parka_f:       { gender: 'f', voices: ['expr-voice-2-f', 'expr-voice-3-f'], speed: 0.97 },
  civ_borean_elder_m:       { gender: 'm', voices: ['expr-voice-3-m', 'expr-voice-4-m'], speed: 0.9 },
  civ_borean_elder_f:       { gender: 'f', voices: ['expr-voice-3-f', 'expr-voice-4-f'], speed: 0.9 },
  civ_borean_miner_m:       { gender: 'm', voices: ['expr-voice-4-m', 'expr-voice-3-m'], speed: 0.98 },
  civ_borean_artisan_f:     { gender: 'f', voices: ['expr-voice-4-f', 'expr-voice-2-f'], speed: 1.0 },
  civ_borean_youth_f:       { gender: 'f', voices: ['expr-voice-5-f', 'expr-voice-4-f'], speed: 1.04 },
  civ_borean_ranger_m:      { gender: 'm', voices: ['expr-voice-2-m', 'expr-voice-5-m'], speed: 1.0 },
  vendor_trader_f:          { gender: 'f', voices: ['expr-voice-2-f', 'expr-voice-3-f'], speed: 0.95 },
  guard_militia_f:          { gender: 'f', voices: ['expr-voice-3-f', 'expr-voice-5-f'], speed: 1.0 },
  guard_militia_m:          { gender: 'm', voices: ['expr-voice-4-m', 'expr-voice-2-m'], speed: 1.0 },
  trainer_instructor_m:     { gender: 'm', voices: ['expr-voice-3-m', 'expr-voice-4-m'], speed: 0.95 },
  colonist_drifter_m:       { gender: 'm', voices: ['expr-voice-2-m', 'expr-voice-4-m'], speed: 0.98 },
  colonist_pilgrim_f:       { gender: 'f', voices: ['expr-voice-4-f', 'expr-voice-3-f'], speed: 0.96 },
};

export interface VoiceChoice {
  voice: KittenVoice;
  /** the friendly name (Bella … Leo) */
  name: string;
  /** the speed handed to the model BEFORE the reference prior (1 = the voice's natural pace) */
  speed: number;
  gender: BodyGender | 'unknown';
}

function h01(id: bigint, salt: number): number {
  // a small deterministic hash: the id folded with a salt through murmur-ish mixing
  let h = Number((id ^ BigInt(salt * 0x9e3779b1)) % 4294967291n) >>> 0;
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** The voice for a versona wearing `bodyKey` (e.g. 'avatar:child_colonist'). */
export function voiceFor(bodyKey: string | null | undefined, versonaId: bigint): VoiceChoice {
  const key = (bodyKey ?? '').replace(/^avatar(-rig)?:/, '');
  const body = BODY_VOICES[key];
  const jitter = 1 + (h01(versonaId, 7) - 0.5) * 0.08; // ±4 %
  if (!body) {
    const v = KITTEN_VOICES[Math.floor(h01(versonaId, 3) * KITTEN_VOICES.length) % KITTEN_VOICES.length];
    return { voice: v, name: VOICE_NAMES[v], speed: +jitter.toFixed(3), gender: 'unknown' };
  }
  const v = body.voices[h01(versonaId, 11) < 0.5 ? 0 : 1];
  return { voice: v, name: VOICE_NAMES[v], speed: +(body.speed * jitter).toFixed(3), gender: body.gender };
}

/** The speed the model receives: the choice × the reference prior for that voice. */
export function modelSpeed(choice: { voice: KittenVoice; speed: number }): number {
  return +(choice.speed * SPEED_PRIORS[choice.voice]).toFixed(4);
}
